import { requestClient } from 'src/lib/services/api/request-api';

// Use any to avoid type conflicts with toast function signature
type ToastFunction = any;

type TranslateFunction = (key: string, options?: any) => string;

/**
 * Service for handling request-related notifications
 * Provides centralized notification logic for request updates
 */
export class RequestNotificationService {
  /**
   * Show notification when a request is updated by customer
   * @param requestId - The ID of the updated request
   * @param t - Translation function
   * @param toast - Toast function from useToast hook
   */
  static async notifyRequestUpdated(
    requestId: string,
    t: TranslateFunction,
    toast: ToastFunction,
  ) {
    try {
      const request = await requestClient.get(requestId);

      // Check if "Needs Attention" was removed
      const wasAttentionRemoved = !request.requested_attention_at;

      if (wasAttentionRemoved) {
        this.showToastNotification(request, t, toast, 'attention_removed');
      } else {
        // Show generic update notification
        this.showToastNotification(request, t, toast, 'status_updated');
      }
    } catch (error) {
      console.error('Failed to show request notification:', error);
    }
  }

  /**
   * Show toast notification with appropriate message
   */
  private static showToastNotification(
    request: any,
    t: TranslateFunction,
    toast: ToastFunction,
    type: 'attention_removed' | 'status_updated',
  ) {
    const messages = {
      attention_removed: {
        title: t('pages/requests:notification.request_ready', { defaultValue: 'Request Ready' }),
        description: t('pages/requests:notification.attention_removed', {
          defaultValue: 'Client removed attention request for "{{title}}". You can resume work.',
          replace: { title: request.title },
        }),
      },
      status_updated: {
        title: t('pages/requests:notification.request_updated', {
          defaultValue: 'Request Updated',
        }),
        description: t('pages/requests:notification.client_updated', {
          defaultValue: 'Client updated request "{{title}}".',
          replace: { title: request.title },
        }),
      },
    };

    const message = messages[type];

    toast({
      title: message.title,
      description: message.description,
      duration: 8000, // 8 seconds
    });

    // Show browser notification if supported and permitted
    this.showBrowserNotification(request, message.title, message.description);
  }

  /**
   * Show native browser notification
   */
  private static showBrowserNotification(request: any, title: string, body: string) {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body,
          icon: '/logo.png',
          tag: `request-${request.id}`,
          requireInteraction: false,
        });
      } catch (error) {
        console.error('Failed to show browser notification:', error);
      }
    }
  }

  /**
   * Request permission for browser notifications
   * Should be called during app initialization or from user settings
   */
  static async requestNotificationPermission(): Promise<NotificationPermission | null> {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        return await Notification.requestPermission();
      } catch (error) {
        console.error('Failed to request notification permission:', error);
        return null;
      }
    }
    return Notification.permission;
  }
}
